import React, { useEffect, useState } from "react";
import { FaEye } from "react-icons/fa";
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import VisualizarView from "./VerFacturasGeneradas";
import FiltroView from "./FiltroFacturasGeneradas";
// IMPORTANTE: Importamos DynamicTable
import DynamicTable from "../../../components/listing/DynamicTable";

const DEFAULT_PAGE_SIZE = 30;

export default function ListarFacturasGeneradas() {
  // ============================
  // PAGINACIÓN
  // ============================
  const [items, setItems] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1); // UI 1-based
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);
  const [cargando, setCargando] = useState(false);

  // ============================
  // FILTROS
  // ============================
  const [filtros, setFiltros] = useState({});
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => setMostrarFiltros(false), mostrarFiltros);

  const limpiarSeleccion = () => {
    const empty = {};
    setFiltros(empty);
    setPaginaActual(1);
    fetchConsulta(0, porPagina, empty);
  };

  // ============================
  // VISUALIZAR
  // ============================
  const [showVisualizar, setShowVisualizar] = useState(false);
  const [idVisualizar, setIdVisualizar] = useState(null);
  useEscClose(() => setShowVisualizar(false), showVisualizar);

  // ============================
  // FETCH
  // ============================
  const fetchConsulta = async (
    page = paginaActual - 1,
    size = porPagina,
    filtrosAplicados = filtros
  ) => {
    setCargando(true);

    try {
      const url = apiRoutes.facturas.listUrl({
        page,
        size,
        ...filtrosAplicados,
      });

      const data = await appFetchJson(url);

      setItems(data.items || []);
      setTotalBackend(data.totalElements || 0);
    } catch (err) {
      console.error("Error cargando facturas", err);
      setItems([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchConsulta(0, porPagina, filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPaginas = Math.max(1, Math.ceil(totalBackend / porPagina));

  const cambiarPagina = (num) => {
    const pagina = Math.min(Math.max(num, 1), totalPaginas);
    setPaginaActual(pagina);
    fetchConsulta(pagina - 1, porPagina);
  };

  // ============================
  // FORMATOS
  // ============================
  const fmt = (v) => (v == null ? "" : Number(v).toLocaleString("es-PY"));

  // ====================================================================
  // DEFINICIÓN DE COLUMNAS (11 COLUMNAS - TOTAL 100%)
  // ====================================================================
  const columns = [
    // 1. CLIENTE (14%)
    { 
      header: "Cliente", 
      accessor: "cliente",
      type: "text", 
      width: "14%", 
      cellClass: "text-xs font-medium text-gray-800 whitespace-normal break-words leading-tight" 
    },
    // 2. N° LIQUIDACIÓN (8%)
    { 
      header: "N° Liq.", 
      accessor: "numeroLiquidacion",
      type: "center", 
      width: "8%",
      render: (row) => row.numeroLiquidacion ?? "-"
    },
    // 3. FACTURA X CLIENTE (10%)
    { 
      header: "Factura Cliente", 
      accessor: "facturaCliente",
      type: "text", 
      width: "10%",
      cellClass: "text-xs text-gray-600 break-words"
    },
    // 4. ESTADO (8%)
    { 
      header: "Estado", 
      accessor: "estado",
      type: "text", 
      width: "8%",
      cellClass: "text-xs font-semibold text-gray-700"
    },
    // 5. NÚMERO (8%)
    { 
      header: "Número", 
      accessor: "numero",
      type: "text", 
      width: "8%",
      cellClass: "text-xs text-gray-600"
    },
    // 6. PRODUCTO (12%)
    { 
      header: "Producto", 
      accessor: "producto",
      type: "text", 
      width: "12%",
      cellClass: "text-xs whitespace-normal break-words leading-tight"
    },
    // 7. CANTIDAD (8%) - Numérico
    { 
      header: "Cantidad", 
      accessor: "qnProducto",
      className: "text-right", // Alinea Título
      width: "8%",
      cellClass: "text-right text-xs font-mono", // Alinea Valor
      render: (row) => fmt(row.qnProducto)
    },
    // 8. PRECIO (8%) - Numérico
    { 
      header: "Precio", 
      accessor: "valorPrecio",
      className: "text-right",
      width: "8%",
      cellClass: "text-right text-xs font-mono",
      render: (row) => fmt(row.valorPrecio)
    },
    // 9. VALOR MONTO (9%) - Numérico
    { 
      header: "Monto Fac", 
      accessor: "valorMontoFac",
      className: "text-right",
      width: "9%",
      cellClass: "text-right text-xs font-mono font-medium text-gray-800",
      render: (row) => fmt(row.valorMontoFac)
    },
    // 10. VALOR IVA (9%) - Numérico
    { 
      header: "IVA Fac", 
      accessor: "valorIvaFac",
      className: "text-right",
      width: "9%",
      cellClass: "text-right text-xs font-mono",
      render: (row) => fmt(row.valorIvaFac)
    },
    // 11. ACCIÓN (6%)
    {
      header: "Acción",
      type: "action", 
      width: "6%",
      render: (row) => (
        <div className="flex justify-center items-center">
          <button
            className="text-gray-500 hover:text-blue-600 p-1"
            title="Visualizar"
            onClick={() => {
              setIdVisualizar(row.id);
              setShowVisualizar(true);
            }}
          >
            <FaEye />
          </button>
        </div>
      )
    }
  ];

  // ============================
  // RENDER
  // ============================
  return (
    <div className="page">
      <ListHeader
        title="Facturas Generadas"
        onFilterClick={() => setMostrarFiltros(true)}
        filters={filtros}
        onClearFilters={limpiarSeleccion}
        showFilter
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPaginaActual(1);
          fetchConsulta(0, size);
        }}
      />

      {mostrarFiltros && (
        <FiltroView
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);
            setPaginaActual(1);
            setMostrarFiltros(false);
            fetchConsulta(0, porPagina, nuevosFiltros);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {cargando && (
        <div className="mt-2 text-sm text-center text-gray-600">
          Cargando facturas...
        </div>
      )}

      {/* TABLA DINÁMICA */}
      {!cargando && (
        <DynamicTable 
          size="L" // 10 columnas -> Breakpoint 1210px
          data={items}
          columns={columns}
        />
      )}

      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={cambiarPagina}
      />

      {/* MODAL VISUALIZAR */}
      {showVisualizar && idVisualizar && (
        <VisualizarView
          id={idVisualizar}
          onClose={() => {
            setShowVisualizar(false);
            setIdVisualizar(null);
          }}
        />
      )}
    </div>
  );
}